import React from "react";
import {
  TextField,
  Button,
  Box,
  Grid,
  CircularProgress,
  Alert,
  InputAdornment,
  IconButton,
  Typography,
  Container,
} from "@mui/material";
import { Visibility, VisibilityOff, Person, Lock } from "@mui/icons-material";
import JSEncrypt from "jsencrypt";
import sha256 from "js-sha256";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { AuthenticationApi } from "../../../API/Authentication/LoginApi";
import { motion, AnimatePresence } from "framer-motion";

const Login: React.FC = () => {
  const [username, setUsername] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [loginError, setLoginError] = useState<string | null>(null);
  const [loader, setLoader] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const navigate = useNavigate();

  const hexToBytes = (hex: string) => {
    const bytes: number[] = [];
    for (let i = 0; i < hex.length; i += 2) {
      bytes.push(parseInt(hex.substr(i, 2), 16));
    }
    return bytes;
  };

  const handleLogin = async () => {
    setLoader(true);
    setLoginError(null);
    try {
      const timestamp = String(Date.now());
      const hash = sha256.sha256(password + timestamp);
      const hashBytes = hexToBytes(hash);
      const hashBase64 = btoa(String.fromCharCode(...hashBytes));
      const data = `${password}:${timestamp}:${hashBase64}`;
      const encrypt = new JSEncrypt();
      encrypt.setPublicKey(
        "MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAg79k2m8PlGNIyJum2uzGqnhdjNqc90SF5FwoOlU7I6Hy6xh36K2qzw1Q5wLcfM+KojFy0xLzKf/u03Wm9S/SmbwvzlXaCq5uYEtVqJBxCz3Y+ZyTRlWyWDJki+OvGYqGbw1EmmKFt2SaOjsE/e+lo6KQpFZwGuaJ1ZKQjCbKRdmkOUAWIjK51a3r8kTnq7XIMf9iXaHX5qvTS/BMcRrXTQ5/JGeuy9OCaJ0S7YrSINZymd0puA0IH87QxDmnUoq+pJwo8l67MFwK7rLyCDSRGwdVY4kLQw8T25k53eoH6+ZUMLydkR4X/ryXYERIw/OwPWpoioHARKzyA/ARA2jkTQIDAQAB"
      );

      const encryptedData = encrypt.encrypt(data);
      if (!encryptedData) throw new Error("Encryption failed. Invalid data.");

      const response = await AuthenticationApi.api.onlineUserAuthentication(
        username,
        encryptedData
      );

      setLoader(false);
      if (response.data && response.data.token) {
        localStorage.setItem("token", response.data.token);
        localStorage.setItem("name", response.data.userDetail.name);
        if (response.data.userDetail.logoUrl) {
          localStorage.setItem("logoUrl", response.data.userDetail.logoUrl);
        }
        navigate("/overview");
      } else {
        setLoginError(response.message || "Access Denied");
      }
    } catch (error) {
      // console.error("Login error:", error);
      setLoader(false);
      setLoginError("Invalid Credentials or Network Error");
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    handleLogin();
  };

  const handleTogglePasswordVisibility = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setShowPassword((prev) => !prev);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
      },
    },
  };

  const imageVariants = {
    hidden: { scale: 1.05, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        duration: 1,
      },
    },
  };

  return (
    <Box className="min-h-screen flex bg-slate-50 overflow-hidden relative">
      <Grid container className="h-screen">
        <Grid
          item
          xs={false}
          sm={5}
          md={6}
          className="hidden sm:flex relative bg-slate-900 overflow-hidden"
        >
          <motion.div
            initial="hidden"
            animate="visible"
            variants={imageVariants}
            className="w-full h-full relative"
          >
            <Box
              component="img"
              src="/assets/login-bg.svg"
              alt="Login Background"
              className="w-full h-full object-cover opacity-100"
              sx={{
                imageRendering: "crisp-edges",
                WebkitBackfaceVisibility: "hidden",
                backfaceVisibility: "hidden",
                transform: "translateZ(0)",
              }}
            />
          </motion.div>
          <Box
            component={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="absolute inset-0 pointer-events-none"
            sx={{
              background:
                "linear-gradient(135deg, rgba(15, 23, 42, 0.4) 0%, rgba(30, 41, 59, 0.3) 50%, rgba(15, 23, 42, 0.4) 100%)",
            }}
          />
        </Grid>

        <Grid
          item
          xs={12}
          sm={7}
          md={6}
          className="flex items-center justify-center p-6 sm:p-8 md:p-12 bg-white relative"
        >
          <Box className="absolute inset-0 opacity-[0.03] bg-[radial-gradient(circle_at_2px_2px,#1e293b_1px,transparent_0)] bg-[length:40px_40px]" />
          <Container maxWidth="sm" className="w-full relative z-10">
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <Box className="w-full max-w-[480px] mx-auto">
                <motion.div variants={itemVariants}>
                  <Box className="mb-10">
                    <Typography
                      variant="h3"
                      component="h1"
                      className="font-bold text-3xl sm:text-4xl text-slate-800 mb-2 tracking-tight"
                      sx={{
                        background: "linear-gradient(135deg, #1e293b 0%, #475569 100%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        backgroundClip: "text",
                      }}
                    >
                      Welcome Back
                    </Typography>
                    <Typography
                      variant="body1"
                      className="text-slate-500 text-base font-normal"
                    >
                      Sign in to continue to your account
                    </Typography>
                  </Box>
                </motion.div>

                <form onSubmit={handleSubmit}>
                  <Box className="flex flex-col gap-6">
                    <motion.div variants={itemVariants}>
                      <Box
                        component={motion.div}
                        whileFocus={{ scale: 1.01 }}
                        transition={{ duration: 0.2 }}
                      >
                        <TextField
                          fullWidth
                          required
                          id="username"
                          label="Username"
                          variant="outlined"
                          value={username}
                          onChange={(e) => setUsername(e.target.value)}
                          placeholder="Enter your username"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "12px",
                              backgroundColor: "#ffffff",
                              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                              "& fieldset": {
                                borderColor: "#e2e8f0",
                                borderWidth: "1.5px",
                                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                              },
                              "&:hover fieldset": {
                                borderColor: "#cbd5e1",
                                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
                              },
                              "&.Mui-focused": {
                                transform: "translateY(-1px)",
                                boxShadow: "0 4px 12px rgba(31, 205, 91, 0.15)",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#1FCD5B",
                                borderWidth: "2px",
                              },
                            },
                            "& .MuiInputLabel-root": {
                              color: "#64748b",
                              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                              "&.Mui-focused": {
                                color: "#1FCD5B",
                              },
                            },
                            "& .MuiInputBase-input": {
                              padding: "14px 16px",
                              fontSize: "0.95rem",
                              color: "#1e293b",
                            },
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <motion.div
                                  animate={{
                                    scale: username ? 1.1 : 1,
                                    color: username ? "#1FCD5B" : "#94a3b8",
                                  }}
                                  transition={{ duration: 0.2 }}
                                >
                                  <Person className="text-xl" />
                                </motion.div>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Box>
                    </motion.div>

                    <motion.div variants={itemVariants}>
                      <Box
                        component={motion.div}
                        whileFocus={{ scale: 1.01 }}
                        transition={{ duration: 0.2 }}
                      >
                        <TextField
                          fullWidth
                          required
                          id="password"
                          label="Password"
                          type={showPassword ? "text" : "password"}
                          variant="outlined"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Enter your password"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "12px",
                              backgroundColor: "#ffffff",
                              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                              "& fieldset": {
                                borderColor: "#e2e8f0",
                                borderWidth: "1.5px",
                                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                              },
                              "&:hover fieldset": {
                                borderColor: "#cbd5e1",
                                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
                              },
                              "&.Mui-focused": {
                                transform: "translateY(-1px)",
                                boxShadow: "0 4px 12px rgba(31, 205, 91, 0.15)",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#1FCD5B",
                                borderWidth: "2px",
                              },
                            },
                            "& .MuiInputLabel-root": {
                              color: "#64748b",
                              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                              "&.Mui-focused": {
                                color: "#1FCD5B",
                              },
                            },
                            "& .MuiInputBase-input": {
                              padding: "14px 16px",
                              fontSize: "0.95rem",
                              color: "#1e293b",
                            },
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <motion.div
                                  animate={{
                                    scale: password ? 1.1 : 1,
                                    color: password ? "#1FCD5B" : "#94a3b8",
                                  }}
                                  transition={{ duration: 0.2 }}
                                >
                                  <Lock className="text-xl" />
                                </motion.div>
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  component={motion.button}
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.95 }}
                                  type="button"
                                  aria-label="toggle password visibility"
                                  onClick={handleTogglePasswordVisibility}
                                  edge="end"
                                  className="text-slate-400 transition-all duration-200 hover:text-[#1FCD5B] hover:bg-[rgba(31,205,91,0.08)]"
                                >
                                  <AnimatePresence mode="wait">
                                    {showPassword ? (
                                      <motion.div
                                        key="visible"
                                        initial={{ rotate: -90, opacity: 0 }}
                                        animate={{ rotate: 0, opacity: 1 }}
                                        exit={{ rotate: 90, opacity: 0 }}
                                        transition={{ duration: 0.2 }}
                                      >
                                        <VisibilityOff fontSize="small" />
                                      </motion.div>
                                    ) : (
                                      <motion.div
                                        key="hidden"
                                        initial={{ rotate: -90, opacity: 0 }}
                                        animate={{ rotate: 0, opacity: 1 }}
                                        exit={{ rotate: 90, opacity: 0 }}
                                        transition={{ duration: 0.2 }}
                                      >
                                        <Visibility fontSize="small" />
                                      </motion.div>
                                    )}
                                  </AnimatePresence>
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Box>
                    </motion.div>

                    <AnimatePresence>
                      {loginError && (
                        <motion.div
                          initial={{ opacity: 0, y: -10, scale: 0.95 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: -10, scale: 0.95 }}
                          transition={{
                            duration: 0.3,
                          }}
                        >
                          <Alert
                            severity="error"
                            className="rounded-xl bg-red-50 text-red-900 border border-red-200 shadow-[0_4px_12px_rgba(220,38,38,0.15)]"
                            sx={{
                              "& .MuiAlert-icon": {
                                color: "#dc2626",
                              },
                            }}
                          >
                            {loginError}
                          </Alert>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    <motion.div variants={itemVariants}>
                      <Button
                        component={motion.button}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        type="submit"
                        variant="contained"
                        fullWidth
                        disabled={loader}
                        className="mt-4 py-3.5 rounded-xl text-white text-base font-semibold normal-case relative overflow-hidden shadow-[0_4px_16px_rgba(31,205,91,0.35)] bg-gradient-to-br from-[#1FCD5B] to-[#1ab84d] hover:from-[#1ab84d] hover:to-[#1ab84d] hover:shadow-[0_6px_20px_rgba(31,205,91,0.45)] disabled:from-[#86efac] disabled:to-[#86efac] disabled:shadow-none disabled:text-white transition-all duration-300"
                        sx={{
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            top: 0,
                            left: "-100%",
                            width: "100%",
                            height: "100%",
                            background:
                              "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)",
                            transition: "left 0.5s",
                          },
                          "&:hover::before": {
                            left: "100%",
                          },
                          "&:active": {
                            transform: "scale(0.98)",
                          },
                        }}
                      >
                        {loader ? (
                          <Box
                            component={motion.div}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="flex items-center gap-3"
                          >
                            <CircularProgress
                              size={20}
                              sx={{
                                color: "#ffffff",
                              }}
                            />
                            <span>Signing in...</span>
                          </Box>
                        ) : (
                          <motion.span
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.1 }}
                          >
                            Sign In
                          </motion.span>
                        )}
                      </Button>
                    </motion.div>
                  </Box>
                </form>
              </Box>
            </motion.div>
          </Container>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Login;
